import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth, homeFor } from "@/context/AuthContext";
import { I18nProvider, useI18n } from "@/i18n/I18nContext";
import AppLayout from "@/components/AppLayout";
import Landing from "@/pages/Landing";
import RegisterYouth from "@/pages/RegisterYouth";
import RegisterAdult from "@/pages/RegisterAdult";
import RequestAccess from "@/pages/RequestAccess";
import BookPage from "@/pages/BookPage";
import MediaPage from "@/pages/MediaPage";
import MasterclassPage from "@/pages/MasterclassPage";
import AboutPage from "@/pages/AboutPage";
import FaqPage from "@/pages/FaqPage";
import PrivacyPage from "@/pages/PrivacyPage";
import LegalPage from "@/pages/LegalPage";
import PressPage from "@/pages/PressPage";
import ContactPage from "@/pages/ContactPage";
import Consent from "@/pages/Consent";
import AmbassadorProfile from "@/pages/AmbassadorProfile";
import Login from "@/pages/Login";
import YouthHome from "@/pages/YouthHome";
import ChapterPage from "@/pages/ChapterPage";
import Diary from "@/pages/Diary";
import Synthesis from "@/pages/Synthesis";
import AccountPage from "@/pages/AccountPage";
import CrmHome from "@/pages/CrmHome";
import ParentHome from "@/pages/ParentHome";
import EducatorHome from "@/pages/EducatorHome";
import AdminHome from "@/pages/AdminHome";

const ToastReset = () => {
  const location = useLocation();
  useEffect(() => { toast.dismiss(); }, [location.pathname]);
  return null;
};

const Protected = ({ roles, children }) => {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  if (loading) {
    return (
      <div className="min-h-screen bg-nuit-900 flex items-center justify-center" data-testid="app-loading">
        <p className="font-fredoka text-xl text-brume">{t('common.loading')}</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/connexion" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return <AppLayout>{children}</AppLayout>;
};

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors offset="72px" />
            <ToastReset />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/inscription" element={<RegisterYouth />} />
              <Route path="/inscription-adulte" element={<RegisterAdult />} />
              <Route path="/demande-acces" element={<RequestAccess />} />
              <Route path="/le-livre" element={<BookPage />} />
              <Route path="/medias" element={<MediaPage />} />
              <Route path="/masterclass" element={<MasterclassPage />} />
              <Route path="/a-propos" element={<AboutPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/confidentialite" element={<PrivacyPage />} />
              <Route path="/mentions-legales" element={<LegalPage />} />
              <Route path="/presse" element={<PressPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/consentement/:token" element={<Consent />} />
              <Route path="/ambassadeur/profil/:token" element={<AmbassadorProfile />} />
              <Route path="/connexion" element={<Login />} />
              <Route path="/tableau-de-bord" element={<Protected roles={["jeune"]}><YouthHome /></Protected>} />
              <Route path="/chapitre/:number" element={<Protected roles={["jeune"]}><ChapterPage /></Protected>} />
              <Route path="/carnet" element={<Protected roles={["jeune"]}><Diary /></Protected>} />
              <Route path="/mon-parcours" element={<Protected roles={["jeune"]}><Synthesis /></Protected>} />
              <Route path="/compte" element={<Protected><AccountPage /></Protected>} />
              <Route path="/parent" element={<Protected roles={["parent"]}><ParentHome /></Protected>} />
              <Route path="/educateur" element={<Protected roles={["educateur"]}><EducatorHome /></Protected>} />
              <Route path="/admin" element={<Protected roles={["admin"]}><AdminHome /></Protected>} />
              <Route path="/admin/crm" element={<Protected roles={["admin"]}><CrmHome /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
